<script setup lang="ts">
    import { useEditCoachingFormStore } from '@/js/stores/edit-coaching-form';
    import CheckboxInput from '../CheckboxInput.vue';
    import FormGroup from '../FormGroup.vue';
    import InputLabel from '../InputLabel.vue';
    import TextInput, { TextInputWidth } from '../TextInput.vue';
    import { computed, ref, watch } from 'vue';
    import TextareaInput from '../TextareaInput.vue';
    import { storeToRefs } from 'pinia';
    import Modal, { ModalSize } from '../Modal.vue';
    import { __ } from '@/js/stores/expressions';
    import SelectInput from '../SelectInput.vue';
    import EchoButton from '../EchoButton.vue';
    import { ChevronDownIcon, PlusIcon, XIcon } from '@lucide/vue';
    import ErrorMessage from '../ErrorMessage.vue';

    const { addReasonCode, removeReasonCode } = useEditCoachingFormStore();
    const { formDraft, nestedReasonCodes, formPoints } = storeToRefs(useEditCoachingFormStore());

    const showAddReasonCodeModal = ref(false);
    const newReasonCode = ref('');
    const newReasonCodeParent = ref<string | undefined>(undefined);

    watch(
        () => formDraft.value?.useReasonCodes,
        (newVal, oldVal) => {
            if (!formDraft.value) return;

            if (typeof oldVal === 'undefined') return;

            if (!newVal) {
                formDraft.value.reasonCodes = undefined;
            } else if (!formDraft.value.reasonCodes) {
                formDraft.value.reasonCodes = {
                    title: '',
                    threshold: 0,
                    description: '',
                    reasonCodeDescription: '',
                    codes: [],
                };
            }
        }
    );

    const thresholdPercentage = computed(() => {
        if (formPoints.value === 0) {
            return 0;
        }

        if (!formDraft.value?.reasonCodes || !formDraft.value?.reasonCodes.threshold) {
            return 0;
        }

        return Math.round((formDraft.value.reasonCodes.threshold / formPoints.value) * 100);
    });

    const addReasonCodeAndCloseModal = () => {
        if (!newReasonCode.value.trim()) {
            return;
        }

        addReasonCode(newReasonCodeParent.value, newReasonCode.value.trim());

        newReasonCode.value = '';
        newReasonCodeParent.value = undefined;
        showAddReasonCodeModal.value = false;
    };
</script>

<template>
    <div v-if="formDraft">
        <FormGroup :help-hint="__('explain', { label: __('coaching-form-reason-codes-header') })">
            <template #header>
                {{ __('coaching-form-reason-codes-header') }}
            </template>
            <template #help>
                <p>
                    {{ __('coaching-form-reason-codes-help-text') }}
                </p>
            </template>
            <CheckboxInput id="form-reason-codes" v-model="formDraft.useReasonCodes">
                {{ __('coaching-form-use-reason-codes') }}
            </CheckboxInput>
        </FormGroup>

        <div v-if="formDraft.useReasonCodes" class="mt-4">
            <div class="mt-4 bg-gray-extralight p-6 rounded-md">
                <FormGroup v-if="formDraft.reasonCodes">
                    <div>
                        <InputLabel for="form-reason-codes-max-points">
                            {{ __('coaching-form-reason-codes-max-points-label') }}
                        </InputLabel>
                        <div class="flex items-center gap-2">
                            <TextInput
                                id="form-reason-codes-max-points"
                                v-model.number="formDraft.reasonCodes.threshold"
                                :width="TextInputWidth.Tiny"
                            />
                            <span class="text-body">
                                {{
                                    __('coaching-form-reason-codes-of-threshold', {
                                        formPoints: String(formPoints),
                                        thresholdPercentage: String(thresholdPercentage),
                                    })
                                }}
                            </span>
                        </div>
                    </div>

                    <div>
                        <InputLabel for="form-reason-codes-title">
                            {{ __('title') }}
                        </InputLabel>
                        <TextInput
                            id="form-reason-codes-title"
                            v-model="formDraft.reasonCodes.title"
                            :width="TextInputWidth.Large"
                        />
                    </div>

                    <div>
                        <InputLabel for="form-reason-codes-description">
                            {{ __('description') }}
                        </InputLabel>
                        <TextareaInput
                            id="form-reason-codes-description"
                            v-model="formDraft.reasonCodes.description"
                        />
                    </div>

                    <div>
                        <InputLabel for="form-reason-codes-reasoncodedescription">
                            {{ __('coaching-form-reason-codes-description') }}
                        </InputLabel>
                        <TextareaInput
                            id="form-reason-codes-reasoncodedescription"
                            v-model="formDraft.reasonCodes.reasonCodeDescription"
                        />
                    </div>

                    <FormGroup>
                        <template #header>
                            {{ __('coaching-form-reason-codes') }}
                        </template>

                        <ErrorMessage validation-key="reasonCodes.codes" />

                        <ul v-if="nestedReasonCodes?.length" class="text-body my-0">
                            <li v-for="item in nestedReasonCodes" :key="item.id" class="my-1">
                                <EchoButton v-if="item.childCodes?.length" size="small">
                                    {{ item.code }}
                                    <template #icon-right><ChevronDownIcon /></template>
                                </EchoButton>
                                <EchoButton v-else size="small" @click="removeReasonCode(item.id)">
                                    {{ item.code }}
                                    <template #icon-right><XIcon /></template>
                                </EchoButton>

                                <ul v-if="item.childCodes?.length" class="ml-6">
                                    <li
                                        v-for="child in item.childCodes"
                                        :key="child.id"
                                        class="my-1"
                                    >
                                        <EchoButton
                                            size="small"
                                            @click="removeReasonCode(child.id)"
                                        >
                                            {{ child.code }}
                                            <template #icon-right><XIcon /></template>
                                        </EchoButton>
                                    </li>
                                </ul>
                            </li>
                        </ul>

                        <div>
                            <EchoButton
                                id="add-reason-code-button"
                                variant="dashed"
                                size="small"
                                @click="showAddReasonCodeModal = true"
                            >
                                <template #icon-left><PlusIcon /></template>
                                {{ __('coaching-form-add-reason-code') }}
                            </EchoButton>
                        </div>
                    </FormGroup>
                </FormGroup>
            </div>
        </div>
    </div>

    <Modal
        :show="showAddReasonCodeModal"
        :size="ModalSize.Small"
        auto-focus="#reason-code-title"
        @close="showAddReasonCodeModal = false"
    >
        <form @submit.prevent="addReasonCodeAndCloseModal()">
            <FormGroup>
                <div>
                    <InputLabel for="reason-code-parent">
                        {{ __('coaching-form-reason-code-parent') }}
                    </InputLabel>
                    <SelectInput id="reason-code-parent" v-model="newReasonCodeParent">
                        <option :value="undefined">
                            {{ __('none') }}
                        </option>
                        <option v-for="item in nestedReasonCodes" :key="item.id" :value="item.id">
                            {{ item.code }}
                        </option>
                    </SelectInput>
                </div>
                <div>
                    <InputLabel for="reason-code-title">
                        {{ __('coaching-form-reason-code-title') }}
                    </InputLabel>
                    <TextInput
                        id="reason-code-title"
                        v-model="newReasonCode"
                        :width="TextInputWidth.Large"
                    />
                </div>
                <div>
                    <EchoButton variant="primary" :disabled="!newReasonCode" type="submit">
                        {{ __('coaching-form-add-reason-code') }}
                    </EchoButton>
                </div>
            </FormGroup>
        </form>
    </Modal>
</template>
